import Vue from "vue";
import _ from "lodash";
import store from "@/store";

var chatTemplate = {
  messages: [],
  unread: 0
};

export default {
  namespaced: true,
  state: {
    initialized: false,
    chatsByQuiz: {},
    spectatorName: null,
    spamWarning: false,
    isChatOpen: false
  },
  mutations: {
    /**
     * Internal use, prevent double-setup ws listeners
     * @param {*} state
     * @param {*} value
     */
    setInitialized(state, value) {
      state.initialized = value;
    },
    markRead(state, quizID) {
      if (state.chatsByQuiz[quizID] == undefined) {
        Vue.set(state.chatsByQuiz, quizID, _.cloneDeep(chatTemplate));
      }

      Vue.set(state.chatsByQuiz[quizID], "unread", 0);
    },
    setChat(state, { quizID, messageList }) {
      if (state.chatsByQuiz[quizID] == undefined) {
        Vue.set(state.chatsByQuiz, quizID, _.cloneDeep(chatTemplate));
      }

      Vue.set(state.chatsByQuiz[quizID], "messages", messageList);
      Vue.set(state.chatsByQuiz[quizID], "unread", messageList.length);
    },
    addChatMessage(state, { quizID, message }) {
      if (state.chatsByQuiz[quizID] == undefined) {
        Vue.set(state.chatsByQuiz, quizID, _.cloneDeep(chatTemplate));
      }

      state.chatsByQuiz[quizID].messages.push(message);
      Vue.set(
        state.chatsByQuiz[quizID],
        "unread",
        state.chatsByQuiz[quizID].unread + 1
      );
    },
    deleteMessage(state, { quizID, messageID }) {
      var chat = state.chatsByQuiz[quizID];
      if (!chat) {
        return;
      }

      var m = _.filter(chat.messages, value => {
        return !(value.id == messageID);
      });

      Vue.set(state.chatsByQuiz[quizID], "messages", m);
    },
    editMessage(state, { quizID, messageID, author, newText }) {
      var chat = state.chatsByQuiz[quizID];
      if (!chat) {
        return;
      }

      _.forEach(chat.messages, value => {
        if (value.id == messageID && value.chatName == author) {
          value.data.text = newText;
          Vue.set(value, "isEdited", true);
        }
      });
    },
    reactToMessage(state, { quizID, messageID, reactor, reaction }) {
      var chat = state.chatsByQuiz[quizID];
      if (!chat) {
        return;
      }

      _.forEach(chat.messages, value => {
        if (value.id == messageID && value.chatName != reactor) {
          if (!value.reactions) {
            Vue.set(value, "reactions", {});
          }
          if (!value.reactions[reaction]) {
            Vue.set(value.reactions, reaction, []);
          }
          // add or remove reaction
          if (!value.reactions[reaction].includes(reactor)) {
            value.reactions[reaction].push(reactor);
          } else {
            var r = _.filter(value.reactions[reaction], m => m != reactor);
            if (r.length > 0) {
              Vue.set(value.reactions, reaction, r);
            } else {
              Vue.delete(value.reactions, reaction);
            }
          }
        }
      });
    },
    spamWarning(state, isWarning) {
      state.spamWarning = isWarning;
    },
    setChatOpen(state, open) {
      state.isChatOpen = open;
    },
    removeAnswerBotMessages(state, quizID) {
      var chat = state.chatsByQuiz[quizID];
      if (!chat) {
        return;
      }

      _.remove(chat.messages, value => {
        return value.author == "answer:bot";
      });
    }
  },
  actions: {
    setChatOpen(me, open) {
      me.commit("setChatOpen", open);
    },
    /**
     * Loads all public quizzes from the database, replacing the old ones
     * @param {*} me
     * @param {*} vm
     */
    markRead(me, quizID) {
      me.commit("markRead", quizID);
    },
    /**
     * Loads all public quizzes from the database, replacing the old ones
     * @param {*} me
     * @param {*} vm
     */
    loadQuizChat(me, vm) {
      me.dispatch("storeCreated", vm);
      vm.$topquiz.sendCommand("chat-getRecent", vm.quizID);
    },
    /**
     * Runs only once (when not initialized),
     * sets up all the necessary topquiz handlers to manage this store
     * @param {*} me
     * @param {*} vm
     */
    storeCreated(me, vm) {
      if (me.state.initialized === true) {
        return;
      }

      me.commit("setInitialized", true);

      vm.$topquiz.on(this, "chat", params => {
        var quizID = params[0];
        var message = params[1];
        me.dispatch("addChatMessage", { quizID, message });
      });

      vm.$topquiz.on(this, "chat-recent", params => {
        var quizID = params[0];
        var messageList = params[1];
        if (messageList == null) {
          messageList = [];
        }

        me.commit("setChat", { quizID, messageList });
      });

      vm.$topquiz.on(this, "chat-spamWarning", () => {
        me.dispatch("spamWarning", true);
      });

      vm.$topquiz.on(this, "chat-delete", params => {
        var quizID = params[0];
        var messageID = params[1];

        me.commit("deleteMessage", { quizID, messageID });
      });

      vm.$topquiz.on(this, "chat-edit", params => {
        var quizID = params[0];
        var messageID = params[1];
        var author = params[2];
        var newText = params[3];

        me.commit("editMessage", { quizID, messageID, author, newText });
      });

      vm.$topquiz.on(this, "chat-react", params => {
        var quizID = params[0];
        var messageID = params[1];
        var reactor = params[2];
        var reaction = params[3];

        me.commit("reactToMessage", { quizID, messageID, reactor, reaction });
      });
    },
    spamWarning(me, isWarning) {
      me.commit("spamWarning", isWarning);
    },
    addChatMessage(me, { quizID, message }) {
      me.commit("addChatMessage", { quizID, message });
    },
    removeAnswerBotMessages(me, quizID) {
      me.commit("removeAnswerBotMessages", quizID);
    }
  },
  getters: {
    quizChat: state => quizID => {
      if (state.chatsByQuiz[quizID] == undefined) {
        return [];
      }

      return state.chatsByQuiz[quizID].messages;
    },
    quizChatUnread: state => quizID => {
      if (state.chatsByQuiz[quizID] == undefined) {
        return 0;
      }

      return state.chatsByQuiz[quizID].unread;
    },
    chatName: state => {
      if (store.getters.isLoggedIn) {
        return store.state.userName;
      }

      if (state.spectatorName == null) {
        state.spectatorName =
          "Spectator#" +
          Vue.prototype.$randomString({ length: 5, alphanumeric: true });
      }

      return state.spectatorName;
    }
  }
};
